import { useNumberFormatter } from '@mint-lib/formatting-hooks';
import { typographyVariants } from '@mint-lib/tw-components';
import type { Chat } from '@mint-sdk/api-ai-assistant';

import {
  CHART_COLORS_IN_ORDER_TAILWIND,
  colorToHex,
  formatChartValue,
} from '../../../helpers/charts.js';

type Props = {
  dataframe: Chat.DataframeType;
  yKey: string;
  xKey: string;
  label?: string;
};

const ScatterLegend = ({ dataframe, yKey, xKey, label }: Props) => {
  const numberFormatter = useNumberFormatter();
  const colors = CHART_COLORS_IN_ORDER_TAILWIND;

  const visibleData = dataframe.filter(
    (frame) => Number(frame?.[yKey]) > 0 && Number(frame?.[xKey]) > 0,
  );

  const legendText = (item: (typeof visibleData)[number], i: number) => {
    const xPart = formatChartValue({
      value: item[xKey],
      numberFormatter,
    });
    const yPart = formatChartValue({
      value: item[yKey],
      numberFormatter,
    });

    return label && dataframe[i][label]
      ? `${item[label]}: ${xPart} - ${yPart}`
      : `${xPart} - ${yPart}`;
  };

  return (
    <div
      data-legend // Property for casting to SVG
      className={
        'flex w-full max-w-175 flex-row flex-wrap items-center justify-start gap-2 pl-10'
      }
    >
      {visibleData?.map((item, i) => (
        <div
          className="flex items-center gap-1"
          key={`${item[xKey]}-${item[yKey]}`}
        >
          <div
            style={{ backgroundColor: colorToHex(colors[i % colors.length]) }}
            className="flex size-2.5 shrink-0 flex-row"
          />

          <p className={typographyVariants({ variant: 'bodyLong01' })}>
            {legendText(item, i)}
          </p>
        </div>
      ))}
    </div>
  );
};

export default ScatterLegend;
